import type { ListingButtonAvailability, ListingButtonKind } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useId } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Checkbox } from '~/shared/ui/checkbox'
import { chooserRows, moveButton, toggleButton } from '../lib/button-chooser'
import { BUTTON_ICONS, BUTTON_LABELS } from './button-kinds'

const LINK = 'font-medium text-brand-subtle-foreground underline underline-offset-4'

const MISSING: Record<Exclude<ListingButtonKind, 'share'>, () => string> = {
  lunch_menu: m.listingButtonsMissingLunchMenu,
  menu: m.listingButtonsMissingMenu,
  phone: m.listingButtonsMissingPhone,
  directions: m.listingButtonsMissingDirections,
  website: m.listingButtonsMissingWebsite,
  instagram: m.listingButtonsMissingInstagram,
}

function MissingHint({
  kind,
  listingId,
}: {
  kind: Exclude<ListingButtonKind, 'share'>
  listingId: string
}) {
  const menuKind = kind === 'lunch_menu' || kind === 'menu'
  return (
    <span className="text-muted-foreground text-xs">
      {MISSING[kind]()}{' '}
      {menuKind ? (
        <Link to="/dashboard/menu" search={{ listingId }} className={LINK}>
          {m.listingButtonsUploadMenu()}
        </Link>
      ) : (
        <Link to="/dashboard/listings/$listingId/edit" params={{ listingId }} className={LINK}>
          {m.listingButtonsAddInPlace()}
        </Link>
      )}
    </span>
  )
}

interface ButtonChooserListProps {
  listingId: string
  chosen: ListingButtonKind[]
  available: ListingButtonAvailability
  disabled: boolean
  onChange: (chosen: ListingButtonKind[]) => void
}

export function ButtonChooserList({
  listingId,
  chosen,
  available,
  disabled,
  onChange,
}: ButtonChooserListProps) {
  const id = useId()

  return (
    <ul className="flex flex-col gap-1.5">
      {chooserRows(chosen, available).map((row) => {
        const Icon = BUTTON_ICONS[row.kind]
        const label = BUTTON_LABELS[row.kind]()
        const checkboxId = `${id}-${row.kind}`
        return (
          <li
            key={row.kind}
            className={cn(
              'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors duration-150 ease-spring',
              row.position === null ? 'border-transparent' : 'border-border bg-card',
            )}
          >
            <Checkbox
              id={checkboxId}
              checked={row.position !== null}
              disabled={disabled || !row.canToggle}
              onCheckedChange={() => onChange(toggleButton(chosen, row.kind, available))}
            />
            <span
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-lg',
                row.position === 0 ? 'bg-foreground/90 text-background' : 'bg-foreground/5',
                !row.available && 'opacity-50',
              )}
            >
              <Icon className="size-4" aria-hidden />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <label
                htmlFor={checkboxId}
                className={cn(
                  'truncate font-medium text-sm',
                  !row.available && 'text-muted-foreground',
                )}
              >
                {label}
              </label>
              {!row.available && row.kind !== 'share' && (
                <MissingHint kind={row.kind} listingId={listingId} />
              )}
            </div>
            {row.position !== null && (
              <div className="flex items-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={m.listingButtonsMoveUp({ label })}
                  disabled={disabled || !row.canMoveUp}
                  onClick={() => onChange(moveButton(chosen, row.kind, -1))}
                >
                  <ChevronUp />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={m.listingButtonsMoveDown({ label })}
                  disabled={disabled || !row.canMoveDown}
                  onClick={() => onChange(moveButton(chosen, row.kind, 1))}
                >
                  <ChevronDown />
                </Button>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
